import React from 'react';
import { Button } from '../ui';
import { TOOLBAR_ICONS } from '../toolbar/ToolbarIcons';

/**
 * MobileMenu - Full screen menu for mobile devices
 * Provides access to Sheets, Layers, Settings, and File operations
 */
const MobileMenu = ({
  isOpen,
  onClose,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSelectAll,
  onDeselectAll,
  onDelete,
  onDetectRooms,
  onCopy,
  onPaste,
  hasClipboard = false,
  hasSelection,
  onShowSheets,
  onShowLayers,
  onShow3D,
  onShowSettings,
  onShowPdf,
  onShowMaterials,
  onShowEstimates,
  onShowInvoices,
  onShowClients,
  onShowCompanyProfile,
  onShowTemplates,
  onShowHelp,
  onExport,
  onExportAnnotatedPDF,
  onSave,
  onLoad,
  onNew,
  onRecent,
  onShare,
  show3D,
  showLayersPanel,
  showSheetsPanel,
  hasPdf,
  floors = [],
  activeFloorId,
  onSelectFloor,
  onAddFloor,
  onRemoveFloor,
  onDuplicateFloor,
  onRenameFloor,
}) => {
  if (!isOpen) return null;

  const handleAction = (action) => {
    action?.();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 900,
        }}
        onClick={onClose}
      />

      {/* Menu Panel */}
      <div
        style={{
          position: 'fixed',
          top: '60px',
          left: '10px',
          right: '10px',
          background: 'rgba(8,12,16,0.98)',
          border: '1px solid rgba(0,200,255,0.2)',
          borderRadius: '12px',
          padding: '16px',
          zIndex: 950,
          maxHeight: 'calc(100vh - 140px)',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
            Menu
          </span>
          <Button variant="ghost" size="small" onClick={onClose}>
            ×
          </Button>
        </div>

        {/* Floors Section */}
        <div style={{
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}>
            <span style={{
              color: '#6080a0',
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Floors
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => {
                  onDuplicateFloor?.(activeFloorId);
                }}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#8899aa',
                  cursor: 'pointer',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                }}
              >
                Copy Floor
              </button>
              <button
                onClick={() => {
                  const name = prompt('Floor name:', `Floor ${floors.length + 1}`);
                  if (name) onAddFloor?.(name);
                }}
                style={{
                  background: 'rgba(0,200,255,0.15)',
                  border: '1px solid rgba(0,200,255,0.3)',
                  color: '#00c8ff',
                  cursor: 'pointer',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                }}
              >
                + Add Floor
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {floors.map((floor) => (
              <div key={floor.id} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <button
                  onClick={() => { onSelectFloor?.(floor.id); onClose(); }}
                  style={{
                    background: floor.id === activeFloorId ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.05)',
                    border: floor.id === activeFloorId ? '1px solid rgba(0,200,255,0.5)' : '1px solid rgba(255,255,255,0.15)',
                    color: floor.id === activeFloorId ? '#00c8ff' : '#8899aa',
                    cursor: 'pointer',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: floor.id === activeFloorId ? '600' : '400',
                  }}
                >
                  {floor.name}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newName = prompt('Rename floor:', floor.name);
                    if (newName && newName.trim()) {
                      onRenameFloor?.(floor.id, newName.trim());
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#8899aa',
                    cursor: 'pointer',
                    padding: '2px',
                    fontSize: '10px',
                    opacity: 0.6,
                  }}
                  title="Rename floor"
                >
                  ✎
                </button>
                {floors.length > 1 && (
                  <button
                    onClick={() => {
                      if (confirm(`Delete "${floor.name}"?`)) {
                        onRemoveFloor?.(floor.id);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ff6666',
                      cursor: 'pointer',
                      padding: '2px',
                      fontSize: '10px',
                      opacity: 0.6,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <MenuButton
            Icon={TOOLBAR_ICONS.undo}
            label="Undo"
            onClick={() => handleAction(onUndo)}
            disabled={!canUndo}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.redo}
            label="Redo"
            onClick={() => handleAction(onRedo)}
            disabled={!canRedo}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.view3d}
            label="3D"
            onClick={() => handleAction(onShow3D)}
            active={show3D}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.layers}
            label="Layers"
            onClick={() => handleAction(onShowLayers)}
            active={showLayersPanel}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.sheets}
            label="Sheets"
            onClick={() => handleAction(onShowSheets)}
            active={showSheetsPanel}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.gear}
            label="Settings"
            onClick={() => handleAction(onShowSettings)}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.pdf}
            label={hasPdf ? "PDF" : "Import PDF"}
            onClick={() => handleAction(onShowPdf)}
            active={hasPdf}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.calculator}
            label="Materials"
            onClick={() => handleAction(onShowMaterials)}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.estimate}
            label="Estimates"
            onClick={() => handleAction(onShowEstimates)}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.invoice}
            label="Invoices"
            onClick={() => handleAction(onShowInvoices)}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.share}
            label="Share"
            onClick={() => handleAction(onShare)}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.clients}
            label="Clients"
            onClick={() => handleAction(onShowClients)}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.company}
            label="Company"
            onClick={() => handleAction(onShowCompanyProfile)}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.templates}
            label="Templates"
            onClick={() => handleAction(onShowTemplates)}
          />
          <MenuButton
            Icon={TOOLBAR_ICONS.help}
            label="Help"
            onClick={() => handleAction(onShowHelp)}
          />
        </div>

        {/* Edit Operations */}
        <div style={{
          paddingTop: '12px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          marginBottom: '16px',
        }}>
          <span style={{
            color: '#6080a0',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px',
            display: 'block',
          }}>
            Edit
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            <MenuButton
              Icon={TOOLBAR_ICONS.selectAll}
              label="Select All"
              onClick={() => handleAction(onSelectAll)}
            />
            <MenuButton
              Icon={TOOLBAR_ICONS.deselect}
              label="Deselect"
              onClick={() => handleAction(onDeselectAll)}
            />
            <MenuButton
              Icon={TOOLBAR_ICONS.delete}
              label="Delete"
              onClick={() => handleAction(onDelete)}
              disabled={!hasSelection}
            />
            <MenuButton
              Icon={TOOLBAR_ICONS.copy}
              label="Copy"
              onClick={() => handleAction(onCopy)}
              disabled={!hasSelection}
            />
            <MenuButton
              Icon={TOOLBAR_ICONS.paste}
              label="Paste"
              onClick={() => handleAction(onPaste)}
              disabled={!hasClipboard}
            />
            <MenuButton
              Icon={TOOLBAR_ICONS.detectRooms}
              label="Detect Rooms"
              onClick={() => handleAction(onDetectRooms)}
            />
          </div>
        </div>

        {/* File Operations */}
        <div style={{
          paddingTop: '12px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}>
          <span style={{
            color: '#6080a0',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '8px',
            display: 'block',
          }}>
            File
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
            <MenuButton
              Icon={TOOLBAR_ICONS.newFile}
              label="New"
              onClick={() => handleAction(onNew)}
            />
            <MenuButton
              Icon={TOOLBAR_ICONS.save}
              label="Save"
              onClick={() => handleAction(onSave)}
            />
            <MenuButton
              Icon={TOOLBAR_ICONS.clock}
              label="Recent"
              onClick={() => handleAction(onRecent)}
            />
            <MenuButton
              Icon={TOOLBAR_ICONS.export}
              label="Export"
              onClick={() => handleAction(onExport)}
            />
            {hasPdf && (
              <MenuButton
                Icon={TOOLBAR_ICONS.pdf}
                label="PDF Export"
                onClick={() => handleAction(onExportAnnotatedPDF)}
              />
            )}
            <label style={{
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '10px',
              color: '#8899aa',
              fontSize: '11px',
              cursor: 'pointer',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              minHeight: '60px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0,200,255,0.1)';
              e.currentTarget.style.borderColor = 'rgba(0,200,255,0.3)';
              e.currentTarget.style.color = '#00c8ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
              e.currentTarget.style.color = '#8899aa';
            }}
            >
              <TOOLBAR_ICONS.folderOpen color="#8899aa" />
              <span>Open</span>
              <input
                type="file"
                accept=".json"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      onLoad?.(event.target.result);
                      onClose();
                    };
                    reader.readAsText(e.target.files[0]);
                  }
                  e.target.value = '';
                }}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * MenuButton - Individual menu button using Button component
 * Supports either emoji icon (string) or SVG Icon component
 */
const MenuButton = ({ icon, Icon, label, onClick, active, disabled }) => (
  <Button
    variant="default"
    active={active}
    disabled={disabled}
    onClick={onClick}
    style={{
      flexDirection: 'column',
      padding: '12px',
      borderRadius: '10px',
      gap: '4px',
      minHeight: '60px',
    }}
  >
    {Icon ? (
      <Icon color={disabled ? '#506070' : active ? '#00c8ff' : '#8899aa'} />
    ) : (
      <span style={{ fontSize: '18px' }}>{icon}</span>
    )}
    <span style={{ fontSize: '11px' }}>{label}</span>
  </Button>
);

export default MobileMenu;
